import { Loader2 } from 'lucide-react'
import { useState } from 'react'

import { lookupPincode } from '@/shared/api/pincode.api'
import { toast } from '@/shared/lib/toast'

const sanitizeCustomerInput = (field, value) => {
  if (field === 'companyName') {
    return value.replace(/[^a-zA-Z0-9\s.,&'()/-]/g, '')
  }
  if (field === 'contactPersonName') {
    return value.replace(/[^a-zA-Z0-9\s.'-]/g, '')
  }
  if (field === 'contactPersonPhone') {
    return value.replace(/\D/g, '').slice(0, 10)
  }
  if (
    field === 'addressLine1' ||
    field === 'addressLine2' ||
    field === 'city' ||
    field === 'state' ||
    field === 'country'
  ) {
    return value.replace(/[^a-zA-Z0-9\s.,#&'()/-]/g, '')
  }
  if (field === 'pincode') {
    return value.replace(/\D/g, '').slice(0, 6)
  }
  if (field === 'gst') {
    return value
      .replace(/[^a-zA-Z0-9]/g, '')
      .slice(0, 15)
      .toUpperCase()
  }
  return value
}

function EditCustomerModalContent({
  customer,
  onOpenChange,
  onSuccess,
  onUpdate,
}) {
  const [form, setForm] = useState({
    companyName: customer.companyName || '',
    contactPersonName: customer.contactPersonName || '',
    contactPersonPhone: customer.contactPersonPhone || '',
    contactPersonEmail: customer.contactPersonEmail || '',
    addressLine1: customer.addressLine1 || '',
    addressLine2: customer.addressLine2 || '',
    city: customer.city || '',
    state: customer.state || '',
    pincode: customer.pincode || '',
    country: customer.country || '',
    gst: customer.gst || '',
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLookingUpPincode, setIsLookingUpPincode] = useState(false)

  const handleChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: sanitizeCustomerInput(field, value),
    }))
  }

  const handlePincodeChange = async (e) => {
    const rawVal = e.target.value
    const pin = rawVal.replace(/\D/g, '').slice(0, 6)

    setForm((prev) => ({
      ...prev,
      pincode: pin,
    }))

    if (pin.length === 6) {
      try {
        setIsLookingUpPincode(true)
        const details = await lookupPincode(pin)
        if (details) {
          setForm((prev) => ({
            ...prev,
            pincode: pin,
            city: details.city || prev.city,
            state: details.state || prev.state,
            country: details.country || prev.country || 'India',
          }))
          toast.success(
            `Location auto-filled: ${details.city}, ${details.state}`,
          )
        } else {
          toast.error('No location found for this pincode')
        }
      } catch (err) {
        console.error('Pincode lookup error:', err)
        toast.error('Failed to fetch location details for this pincode')
      } finally {
        setIsLookingUpPincode(false)
      }
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!form.companyName?.trim()) {
      toast.error('Company Name is required')
      return
    }
    if (!/^[a-zA-Z0-9\s.,&'()/-]+$/.test(form.companyName.trim())) {
      toast.error('Company Name must be alphanumeric')
      return
    }
    if (!form.contactPersonName?.trim()) {
      toast.error('Contact Person Name is required')
      return
    }
    if (!/^[a-zA-Z0-9\s.'-]+$/.test(form.contactPersonName.trim())) {
      toast.error('Contact Person Name must be alphanumeric')
      return
    }
    if (!form.contactPersonPhone?.trim()) {
      toast.error('Contact Person Phone is required')
      return
    }
    if (!/^\d{10}$/.test(form.contactPersonPhone.trim())) {
      toast.error('Phone number must be numeric and exactly 10 digits')
      return
    }
    if (!form.contactPersonEmail?.trim()) {
      toast.error('Contact Person Email is required')
      return
    }
    if (
      !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/i.test(
        form.contactPersonEmail.trim(),
      )
    ) {
      toast.error('Please enter a valid email address')
      return
    }
    if (!form.addressLine1?.trim()) {
      toast.error('Address Line 1 is required')
      return
    }
    if (!form.city?.trim()) {
      toast.error('City is required')
      return
    }
    if (!form.state?.trim()) {
      toast.error('State is required')
      return
    }
    if (!form.pincode?.trim()) {
      toast.error('Pincode is required')
      return
    }
    if (!form.country?.trim()) {
      toast.error('Country is required')
      return
    }
    if (
      form.gst &&
      form.gst.trim() &&
      !/^[a-zA-Z0-9]+$/.test(form.gst.trim())
    ) {
      toast.error('GST must be alphanumeric')
      return
    }

    try {
      setIsSubmitting(true)
      await onUpdate(customer._id, form)
      onSuccess?.()
      onOpenChange(false)
    } catch {
      // Error handled by store or caller
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-3 sm:p-4">
      <div className="w-full max-w-2xl rounded-2xl sm:rounded-3xl bg-white p-4 sm:p-7 shadow-xl max-h-[92vh] overflow-y-auto">
        {/* Modal Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-slate-900">
              Edit Customer
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              Update customer details.
            </p>
          </div>

          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="rounded-full bg-slate-950 px-6 py-3 text-sm font-semibold text-white hover:bg-slate-800"
          >
            Close
          </button>
        </div>

        {/* Customer Form */}
        <form onSubmit={handleSubmit}>
          <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-2">
            {/* Company Name */}
            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700">
                Company Name
              </label>
              <input
                type="text"
                value={form.companyName}
                onChange={(e) => handleChange('companyName', e.target.value)}
                placeholder="Enter company name"
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-slate-400"
              />
            </div>

            {/* Contact Person Name */}
            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700">
                Contact Person Name
              </label>
              <input
                type="text"
                value={form.contactPersonName}
                onChange={(e) =>
                  handleChange('contactPersonName', e.target.value)
                }
                placeholder="Enter contact person name"
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-slate-400"
              />
            </div>

            {/* Contact Person Phone Number */}
            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700">
                Contact Person Phone Number
              </label>
              <input
                type="tel"
                maxLength={10}
                value={form.contactPersonPhone}
                onChange={(e) =>
                  handleChange('contactPersonPhone', e.target.value)
                }
                placeholder="Enter 10-digit phone number"
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-slate-400"
              />
            </div>

            {/* Contact Person Email ID */}
            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700">
                Contact Person Email ID
              </label>
              <input
                type="email"
                value={form.contactPersonEmail}
                onChange={(e) =>
                  handleChange('contactPersonEmail', e.target.value)
                }
                placeholder="Enter email address"
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-slate-400"
              />
            </div>

            {/* Address Line 1 */}
            <div className="md:col-span-2">
              <label className="mb-2 block text-sm font-medium text-slate-700">
                Address Line 1
              </label>
              <input
                type="text"
                value={form.addressLine1}
                onChange={(e) => handleChange('addressLine1', e.target.value)}
                placeholder="Enter address line 1"
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-slate-400"
              />
            </div>

            {/* Address Line 2 */}
            <div className="md:col-span-2">
              <label className="mb-2 block text-sm font-medium text-slate-700">
                Address Line 2
                <span className="ml-1 text-xs font-normal text-slate-400">
                  (Optional)
                </span>
              </label>
              <input
                type="text"
                value={form.addressLine2}
                onChange={(e) => handleChange('addressLine2', e.target.value)}
                placeholder="Enter address line 2"
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-slate-400"
              />
            </div>

            {/* City */}
            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700">
                City
              </label>
              <input
                type="text"
                value={form.city}
                onChange={(e) => handleChange('city', e.target.value)}
                placeholder="Enter city"
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-slate-400"
              />
            </div>

            {/* State */}
            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700">
                State
              </label>
              <input
                type="text"
                value={form.state}
                onChange={(e) => handleChange('state', e.target.value)}
                placeholder="Enter state"
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-slate-400"
              />
            </div>

            {/* Pincode */}
            <div>
              <label className="mb-2 flex items-center justify-between text-sm font-medium text-slate-700">
                <span>Pincode</span>
                {isLookingUpPincode && (
                  <span className="flex items-center gap-1 text-xs font-normal text-blue-600">
                    <Loader2 className="size-3 animate-spin" />
                    Fetching...
                  </span>
                )}
              </label>
              <div className="relative">
                <input
                  type="text"
                  maxLength={6}
                  value={form.pincode}
                  onChange={handlePincodeChange}
                  placeholder="Enter 6-digit pincode"
                  className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-slate-400"
                />
                {isLookingUpPincode && (
                  <div className="absolute right-3.5 top-1/2 -translate-y-1/2">
                    <Loader2 className="size-4 animate-spin text-slate-400" />
                  </div>
                )}
              </div>
            </div>

            {/* Country */}
            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700">
                Country
              </label>
              <input
                type="text"
                value={form.country}
                onChange={(e) => handleChange('country', e.target.value)}
                placeholder="Enter country"
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-slate-400"
              />
            </div>

            {/* GST */}
            <div className="md:col-span-2">
              <label className="mb-2 block text-sm font-medium text-slate-700">
                GST
                <span className="ml-1 text-xs font-normal text-slate-400">
                  (Optional)
                </span>
              </label>
              <input
                type="text"
                maxLength={15}
                value={form.gst}
                onChange={(e) => handleChange('gst', e.target.value)}
                placeholder="Enter GST number if available"
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm uppercase outline-none focus:border-slate-400"
              />
            </div>
          </div>

          {/* Modal Actions */}
          <div className="mt-7 flex flex-col-reverse sm:flex-row sm:justify-end gap-2.5 sm:gap-3">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="w-full sm:w-auto rounded-full border border-slate-200 px-6 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full sm:w-auto rounded-full bg-slate-950 px-7 py-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export function EditCustomerModal({
  open,
  onOpenChange,
  customer,
  onSuccess,
  onUpdate,
}) {
  if (!open || !customer) return null

  return (
    <EditCustomerModalContent
      key={customer._id || customer.id}
      customer={customer}
      onOpenChange={onOpenChange}
      onSuccess={onSuccess}
      onUpdate={onUpdate}
    />
  )
}
